import experiment from 'experiment';
import {
  leftBarAiToolsClickOnToolsEntryPoint,
  componentAddedToStage,
  componentRemoved,
  aiAssistantSelectedSrc38Evid2100,
} from '@wix/bi-logger-editor-data/v2';
import { AIAssistantApiKey, AiWriterApiKey } from '#packages/apis';
import * as stateManagement from '#packages/stateManagement';
import constants from '#packages/constants';
import { waitForAddedCompRef } from '#packages/componentsAddUtils';
import { biLogger } from '#packages/util';
import { layoutUtils } from '#packages/layoutUtils';

import getTextCompSerialized from './textCompSerialized';

import type { EditorAPI } from '#packages/editorAPI';

export interface AiPageToolsItem {
  automationId: string;
  title: string;
  description: string;
  symbolName: string;
  condition(): boolean;
  onClick(): void;
}

export interface AiPageToolsCategory {
  title: string;
  items: AiPageToolsItem[];
}

const ORIGIN = 'ai_tools_left_bar';
const newContentForAI: boolean = experiment.isOpen('se_newContentForAI');
export const getAiPageToolsList = (
  editorAPI: EditorAPI,
): AiPageToolsCategory[] => {
  return [
    {
      title: 'ai_tools_improve_page_section_header',
      items: [
        {
          automationId: 'ai-tools-page-writer',
          title: newContentForAI
            ? 'ai_tools_generate_improve_page_section_create_text_title'
            : 'ai_tools_improve_page_section_create_text_title',
          description: newContentForAI
            ? 'ai_tools_generate_improve_page_section_create_text_body'
            : 'ai_tools_improve_page_section_create_text_body',
          symbolName: 'editSparkles',
          condition() {
            return experiment.getValue('se_aiToolsLeftBar') === 'C';
          },
          onClick() {
            biLogger.report(
              leftBarAiToolsClickOnToolsEntryPoint({
                targetName: 'Ai Page Writer',
              }),
            );

            editorAPI.host.getAPI(AiWriterApiKey).openAiWriterPanel({
              origin: ORIGIN,
            });
          },
        },
      ],
    },
    {
      title: 'ai_tools_more_tools_section_header',
      items: [
        {
          automationId: 'ai-tools-text-generator',
          title: newContentForAI
            ? 'ai_tools_generate_more_tools_section_text_generator_title'
            : 'ai_tools_more_tools_section_text_generator_title',
          description: newContentForAI
            ? 'ai_tools_generate_more_tools_section_text_generator_body'
            : 'ai_tools_more_tools_section_text_generator_body',
          symbolName: 'aiToolsTextCreator',
          condition() {
            return (
              experiment.isOpen('se_aiTextGenerator') ||
              experiment.isOpen('se_combinedTextToolsAndCreatorV2')
            );
          },
          async onClick() {
            biLogger.report(
              leftBarAiToolsClickOnToolsEntryPoint({
                targetName: 'Ai Text Generator',
              }),
            );

            const text = 'I&rsquo;m a paragraph';
            const containerToAddTo = editorAPI.pages.getFocusedPage();
            const textCompDef = getTextCompSerialized({
              text,
            });

            const position =
              layoutUtils.getPositionInsideParentCloseToViewportCenter(
                editorAPI,
                stateManagement.domMeasurements.selectors.getViewPort(
                  editorAPI.store.getState(),
                  true,
                ),
                containerToAddTo,
                textCompDef,
              );

            const compDefWithPosition = getTextCompSerialized({
              text,
              position,
            });

            const textCompRef = await waitForAddedCompRef(
              editorAPI.components.add(containerToAddTo, compDefWithPosition),
            );

            biLogger.report(
              componentAddedToStage({
                origin: ORIGIN,
                adding_method: 'ai_tools_flow',
                page_id: containerToAddTo.id,
                component_id: textCompRef.id,
                component_type: compDefWithPosition.componentType,
              }),
            );

            await editorAPI.waitForChangesAppliedAsync();

            editorAPI.selection.selectComponentByCompRef(textCompRef);

            editorAPI.openComponentPanel(
              constants.componentPanels.aiTextGenerator,
              {
                origin: ORIGIN,
                onUnmount: () => {
                  const htmlContent =
                    editorAPI.components.data.get(textCompRef).text ?? '';
                  const updatedText = htmlContent.replace(/(<([^>]+)>)/gi, '');

                  if (updatedText === text) {
                    editorAPI.components.remove(textCompRef);

                    biLogger.report(
                      componentRemoved({
                        removal_method: 'auto_deletion_ai_tools_flow',
                        page_id: containerToAddTo.id,
                        component_id: textCompRef.id,
                        component_type: compDefWithPosition.componentType,
                      }),
                    );
                  }
                },
              },
            );
          },
        },
        {
          automationId: 'ai-tools-image-creator',
          title: 'ai_tools_more_tools_section_image_generator_title',
          description: 'ai_tools_more_tools_section_image_generator_body',
          symbolName: 'aiToolsImageCreator',
          condition() {
            return experiment.isOpen('se_aiImageCreatorLeftPanel');
          },
          onClick() {
            editorAPI.mediaServices.generateWPhoto(undefined, ORIGIN);
          },
        },
        {
          automationId: 'ai-tools-section-generator',
          title: experiment.isOpen('se_newContentForAI')
            ? 'ai_tools_generate_more_tools_section_generator_title'
            : 'ai_tools_more_tools_section_generator_generate_title',
          description: newContentForAI
            ? 'ai_tools_generate_more_tools_section_generator_body'
            : 'ai_tools_more_tools_section_generator_body',
          symbolName: 'aiToolsSectionCreator',
          condition() {
            return experiment.isOpen('se_createSectionWithAI');
          },
          onClick() {
            biLogger.report(
              leftBarAiToolsClickOnToolsEntryPoint({
                targetName: 'Ai Section Generator',
              }),
            );

            editorAPI.store.dispatch(
              stateManagement.panels.actions.openLeftPanel(
                constants.ROOT_COMPS.LEFTBAR.ADD_SECTION_PANEL_NAME,
                {
                  origin: ORIGIN,
                  shouldReplaceSingleBlankSection: true,
                  emptyStateBlankSectionIndex: 1,
                  category: 'ai_creator',
                },
              ),
            );
          },
        },
        {
          automationId: 'ai-tools-design-assistant',
          title: experiment.isOpen('se_newContentForAI')
            ? 'ai_tools_generate_more_tools_section_theme_assistant_title'
            : 'ai_tools_more_tools_section_theme_assistant_title',
          description: 'ai_tools_more_tools_section_theme_assistant_body',
          symbolName: 'aiDesignAssistant',
          condition: () => experiment.isOpen('se_aiThemeAssistant'),
          onClick() {
            const aiAssistantAPI = editorAPI.host.getAPI(AIAssistantApiKey);

            biLogger.report(
              aiAssistantSelectedSrc38Evid2100({
                target: 'theme ai assistant',
                origin: 'ai_hub',
              }),
            );

            aiAssistantAPI.openAIAssistantPanel({
              origin: ORIGIN,
              assistantId: 'designAssistant',
              leavePanelsOpen: false,
            });
          },
        },
      ],
    },
  ];
};
